import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Alert, Button, Card, CardBody, Container } from "reactstrap";

import AdatisLogo from "../../../Components/Common/AdatisLogo";
import {
  createPublicBookingAppointment,
  getPublicBookingProfessionals,
  getPublicBookingServices,
  PublicBookingAppointment,
  PublicBookingAvailabilitySlot,
  PublicBookingClubTeaser,
  PublicBookingCustomerPayload,
  PublicBookingProfessional,
  PublicBookingReviewSummary,
  PublicBookingService,
} from "../../../services/publicBookingService";
import BookingPlgBadge from "./BookingPlgBadge";
import CatalogStep from "./CatalogStep";
import DateTimeStep from "./DateTimeStep";
import ProfessionalStep from "./ProfessionalStep";
import SuccessStep from "./SuccessStep";
import SummaryStep from "./SummaryStep";

type BookingWizardStep = "services" | "professionals" | "datetime" | "summary" | "success";

const PublicBookingPage: React.FC = () => {
  const { t } = useTranslation();
  const { token = "" } = useParams<{ token: string }>();

  const [step, setStep] = useState<BookingWizardStep>("services");
  const [services, setServices] = useState<PublicBookingService[]>([]);
  const [professionals, setProfessionals] = useState<PublicBookingProfessional[]>([]);
  const [selectedService, setSelectedService] = useState<PublicBookingService | null>(null);
  const [selectedProfessional, setSelectedProfessional] = useState<PublicBookingProfessional | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<PublicBookingAvailabilitySlot | null>(null);
  const [confirmedAppointment, setConfirmedAppointment] = useState<PublicBookingAppointment | null>(null);
  const [clubTeaser, setClubTeaser] = useState<PublicBookingClubTeaser | null>(null);
  const [showPlgBadge, setShowPlgBadge] = useState(false);
  const [reviewSummary, setReviewSummary] = useState<PublicBookingReviewSummary | null>(null);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [professionalsLoading, setProfessionalsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [slotUnavailableAlert, setSlotUnavailableAlert] = useState<string | null>(null);

  document.title = t("public_booking.pageTitle");

  const loadServices = useCallback(async (): Promise<void> => {
    if (!token) {
      setNotFound(true);
      setServicesLoading(false);
      return;
    }

    setServicesLoading(true);
    setLoadError(null);
    setNotFound(false);

    try {
      const response = await getPublicBookingServices(token);
      setServices(response.data ?? []);
      setShowPlgBadge(response._metadata?.is_powered_by_adatis === true);
      setReviewSummary(response._metadata?.review_summary ?? null);
    } catch (error) {
      const apiError = error as { code?: string };

      if (apiError.code === "NOT_FOUND") {
        setNotFound(true);
        return;
      }

      setLoadError(t("public_booking.errors.loadServices"));
    } finally {
      setServicesLoading(false);
    }
  }, [token, t]);

  const loadProfessionals = useCallback(async (serviceId?: number): Promise<void> => {
    if (!token) {
      return;
    }

    setProfessionalsLoading(true);
    setLoadError(null);

    try {
      const response = await getPublicBookingProfessionals(
        token,
        serviceId ? { service_id: serviceId } : undefined
      );
      setProfessionals(response.data ?? []);
    } catch (error) {
      const apiError = error as { code?: string };

      if (apiError.code === "NOT_FOUND") {
        setNotFound(true);
        return;
      }

      setLoadError(t("public_booking.errors.loadProfessionals"));
    } finally {
      setProfessionalsLoading(false);
    }
  }, [token, t]);

  useEffect(() => {
    void loadServices();
  }, [loadServices]);

  useEffect(() => {
    if (step === "professionals" && selectedService?.id) {
      void loadProfessionals(selectedService.id);
    }
  }, [step, selectedService?.id, loadProfessionals]);

  const stepTitle = useMemo(() => {
    switch (step) {
      case "services":
        return t("public_booking.steps.services");
      case "professionals":
        return t("public_booking.steps.professionals");
      case "datetime":
        return t("public_booking.steps.datetime");
      case "summary":
        return t("public_booking.steps.summary");
      case "success":
        return t("public_booking.steps.success");
      default:
        return t("public_booking.subtitle");
    }
  }, [step, t]);

  const handleSelectService = (service: PublicBookingService): void => {
    setSelectedService(service);
    setSelectedProfessional(null);
    setSelectedSlot(null);
    setSlotUnavailableAlert(null);
    setSubmitError(null);
    setStep("professionals");
  };

  const handleSelectProfessional = (professional: PublicBookingProfessional): void => {
    setSelectedProfessional(professional);
    setSelectedSlot(null);
    setSlotUnavailableAlert(null);
    setSubmitError(null);
  };

  const handleContinue = (): void => {
    if (step === "professionals" && selectedProfessional) {
      setStep("datetime");
      return;
    }

    if (step === "datetime" && selectedSlot) {
      setSubmitError(null);
      setSlotUnavailableAlert(null);
      setStep("summary");
    }
  };

  const handleBackFromSummary = (): void => {
    setSubmitError(null);
    setStep("datetime");
  };

  const handleBackFromDatetime = (): void => {
    setSelectedSlot(null);
    setSlotUnavailableAlert(null);
    setStep("professionals");
  };

  const handleSubmitBooking = async (customer: PublicBookingCustomerPayload): Promise<void> => {
    if (!token || !selectedService || !selectedProfessional || !selectedSlot) {
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    setSlotUnavailableAlert(null);

    try {
      const response = await createPublicBookingAppointment(token, {
        ...customer,
        service_id: selectedService.id,
        professional_id: selectedProfessional.id,
        start_datetime: selectedSlot.start_datetime,
        end_datetime: selectedSlot.end_datetime,
      });

      setConfirmedAppointment(response.data);
      if (response._metadata?.is_powered_by_adatis === true) {
        setShowPlgBadge(true);
      }
      setClubTeaser(response._metadata?.club_teaser ?? null);

      setStep("success");
    } catch (error) {
      const apiError = error as { code?: string; data?: string };

      if (apiError.code === "SLOT_UNAVAILABLE" || apiError.code === "INVALID_TIME") {
        setSlotUnavailableAlert(t("public_booking.errors.slotUnavailable"));
        setSelectedSlot(null);
        setStep("datetime");
        return;
      }

      if (apiError.code === "PUBLIC_BOOKING_DEPOSIT_NOT_ALLOWED") {
        setSubmitError(t("public_booking.errors.depositNotAllowed"));
        return;
      }

      setSubmitError(apiError.data || t("public_booking.errors.confirmFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  const canContinue = (step === "professionals" && selectedProfessional !== null)
    || (step === "datetime" && selectedSlot !== null);

  return (
    <div className="auth-page-wrapper auth-bg-cover py-5 d-flex justify-content-center align-items-center min-vh-100">
      <Container style={{ maxWidth: 720 }}>
        <div className="text-center mb-4">
          <AdatisLogo variant="light" height={36} linkTo={false} />
        </div>

        <Card className="border-0 shadow">
          <CardBody className="p-4 p-md-5">
            {notFound ? (
              <div className="text-center py-4">
                <div className="avatar-md mx-auto mb-3">
                  <span className="avatar-title rounded-circle bg-light text-danger fs-24">
                    <i className="ri-link-unlink" />
                  </span>
                </div>
                <h4 className="mb-2">{t("public_booking.errors.notFoundTitle")}</h4>
                <p className="text-muted mb-0">{t("public_booking.errors.notFoundDescription")}</p>
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <p className="text-muted mb-1">{t("public_booking.subtitle")}</p>
                  <h4 className="mb-0">{stepTitle}</h4>
                  {reviewSummary ? (
                    <p className="text-warning mb-0 mt-2" aria-label={t("public_booking.reviewSummary.aria", {
                      average: reviewSummary.average,
                      count: reviewSummary.count,
                    })}>
                      {"★".repeat(Math.round(reviewSummary.average))}
                      <span className="text-muted">
                        {"★".repeat(Math.max(0, 5 - Math.round(reviewSummary.average)))}
                      </span>
                      <span className="text-muted ms-2 small">
                        {t("public_booking.reviewSummary.label", {
                          average: reviewSummary.average.toFixed(1),
                          count: reviewSummary.count,
                        })}
                      </span>
                    </p>
                  ) : null}
                </div>

                {loadError ? (
                  <Alert color="danger" className="mb-4">
                    {loadError}
                  </Alert>
                ) : null}

                {slotUnavailableAlert && step === "datetime" ? (
                  <Alert color="warning" className="mb-4">
                    {slotUnavailableAlert}
                  </Alert>
                ) : null}

                {step === "services" ? (
                  <CatalogStep
                    services={services}
                    loading={servicesLoading}
                    selectedService={selectedService}
                    onSelectService={handleSelectService}
                  />
                ) : null}

                {step === "professionals" && selectedService ? (
                  <ProfessionalStep
                    service={selectedService}
                    professionals={professionals}
                    loading={professionalsLoading}
                    selectedProfessional={selectedProfessional}
                    onSelectProfessional={handleSelectProfessional}
                    onBack={() => {
                      setStep("services");
                      setSelectedProfessional(null);
                      setSelectedSlot(null);
                    }}
                  />
                ) : null}

                {step === "datetime" && selectedService && selectedProfessional ? (
                  <DateTimeStep
                    token={token}
                    service={selectedService}
                    professional={selectedProfessional}
                    selectedSlot={selectedSlot}
                    onSelectSlot={setSelectedSlot}
                    onBack={handleBackFromDatetime}
                  />
                ) : null}

                {step === "summary" && selectedService && selectedProfessional && selectedSlot ? (
                  <SummaryStep
                    service={selectedService}
                    professional={selectedProfessional}
                    slot={selectedSlot}
                    submitting={submitting}
                    submitError={submitError}
                    onBack={handleBackFromSummary}
                    onSubmit={(customer) => {
                      void handleSubmitBooking(customer);
                    }}
                  />
                ) : null}

                {step === "success" && confirmedAppointment && selectedService && selectedProfessional ? (
                  <SuccessStep
                    appointment={confirmedAppointment}
                    service={selectedService}
                    professional={selectedProfessional}
                    clubTeaser={clubTeaser}
                  />
                ) : null}

                {canContinue ? (
                  <div className="mt-4 d-grid">
                    <Button color="primary" size="lg" onClick={handleContinue}>
                      {t("public_booking.common.continue")}
                    </Button>
                  </div>
                ) : null}
              </>
            )}
          </CardBody>
        </Card>

        {!notFound && showPlgBadge ? <BookingPlgBadge visible /> : null}
      </Container>
    </div>
  );
};

export default PublicBookingPage;
